<template>
  <div class="header-navigation">
    <v-responsive max-width="1320" class="mx-auto">
      <v-container>
        <!-- Mobile Layout (!lgAndUp) -->
        <template v-if="!lgAndUp">
          <div class="header-navigation mobile-header">
            <v-container>
              <v-row align="center" class="mobile-top-row">
                <v-col cols="auto">
                  <h5
                    class="go-back text-white"
                    @click="handleNavigation"
                    role="button"
                    tabindex="0"
                    @keydown.enter.space.prevent="handleNavigation"
                  >
                    {{ navigationText }}
                  </h5>
                </v-col>
                <v-spacer></v-spacer>
                <v-col
                  cols="auto"
                  class="header-navigation-btn-container d-flex justify-end"
                >
                  <ShareButton
                    :title="props.report?.title"
                    :description="`Check out this EdReports material review: ${props.report?.title || ''}`"
                    :report-slug="props.report?.slug"
                    button-variant="text"
                    button-density="compact"
                    button-color="white"
                  />
                  <v-btn
                    variant="text"
                    density="compact"
                    color="white"
                    append-icon="mdi-printer-outline"
                    @click="printPage"
                  >
                    Print
                  </v-btn>
                </v-col>
              </v-row>
              <v-row class="mobile-grades-row">
                <v-col cols="12">
                  <v-expansion-panels
                    v-model="mobileGradeNavExpanded"
                    class="custom-panels mobile-grade-panels"
                    elevation="0"
                  >
                    <v-expansion-panel elevation="0">
                      <v-expansion-panel-title
                        class="pa-0 mobile-grade-panel-title"
                      >
                        <div
                          class="d-flex justify-space-between align-center py-1 w-100 px-2"
                        >
                          <h5 class="text-white font-weight-bold">
                            Full Reports by Grade
                          </h5>
                        </div>
                      </v-expansion-panel-title>
                      <v-expansion-panel-text
                        class="mobile-grade-panel-content"
                      >
                        <div class="grades-navigation mobile-grades-inside">
                          <router-link
                            v-for="grade in sortedReportGrades"
                            :key="grade.slug"
                            :to="{
                              name: 'report-grade-overview',
                              params: {
                                id: report.slug,
                                grade: grade.slug,
                              },
                            }"
                            custom
                            v-slot="{ navigate, isActive }"
                          >
                            <v-btn
                              color="white"
                              class="grades-navigation-button font-weight-bold"
                              :class="{
                                'v-btn--active':
                                  isActive || route.params.grade === grade.slug,
                              }"
                              @click="navigate"
                              block
                            >
                              {{ grade.shortcode }}
                            </v-btn>
                          </router-link>
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </template>

        <!-- Desktop Layout (lgAndUp) -->
        <template v-else>
          <v-row align="center">
            <v-col cols="2">
              <h5
                class="go-back text-white"
                @click="handleNavigation"
                role="button"
                tabindex="0"
                @keydown.enter.space.prevent="handleNavigation"
              >
                {{ navigationText }}
              </h5>
            </v-col>
            <v-col cols="7" class="text-center">
              <div
                class="grades-navigation"
                role="group"
                aria-labelledby="desktop-grade-nav-label"
              >
                <span
                  class="grades-navigation-title"
                  id="desktop-grade-nav-label"
                  >Full Reports:</span
                >
                <router-link
                  v-for="grade in sortedReportGrades"
                  :key="grade.slug"
                  :to="{
                    name: 'report-grade-overview',
                    params: {
                      id: report.slug,
                      grade: grade.slug,
                    },
                  }"
                  custom
                  v-slot="{ navigate, isActive }"
                >
                  <v-btn
                    color="white"
                    class="grades-navigation-button font-weight-bold"
                    :class="{
                      'v-btn--active':
                        isActive || route.params.grade === grade.slug,
                    }"
                    @click="navigate"
                  >
                    {{ grade.shortcode }}
                  </v-btn>
                </router-link>
              </div>
            </v-col>
            <v-col cols="3" class="d-flex justify-end">
              <ShareButton
                :title="props.report?.title"
                :description="`Check out this EdReports material review: ${props.report?.title || ''}`"
                :report-slug="props.report?.slug"
                color="white"
                variant="text"
              />
              <v-btn
                color="white"
                append-icon="mdi-printer-outline"
                @click="printPage"
              >
                Print
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-responsive>
  </div>
</template>

<script setup lang="ts">
  import { useRoute, useRouter } from 'vue-router';
  import { useDisplay } from 'vuetify';
  import { ref, computed } from 'vue';
  import { usePrintState } from '../../composables/usePrintState';
  import type { Report } from '../../store';
  import ShareButton from '../shared/ShareButton.vue';

  const props = defineProps<{
    report: Report;
  }>();

  const route = useRoute();
  const router = useRouter();

  const display = useDisplay();
  const lgAndUp = computed(() => display.lgAndUp.value);
  const mobileGradeNavExpanded = ref<number | null>(null);

  // Use the print state composable
  const { triggerPrint } = usePrintState();

  // Print function that expands all ReadMore components
  const printPage = triggerPrint;

  const sortedReportGrades = computed(() => {
    if (!props.report || !Array.isArray(props.report.report_grades)) {
      return [];
    }
    return [...props.report.report_grades]
      .filter((grade) => grade.slug && grade.slug.trim()) // Filter out grades without valid slug
      .sort((a, b) => a.order - b.order);
  });

  function handleNavigation() {
    if (route.name === 'report-overview') {
      // If on ReportOverview page, go back to all reports
      router.push('/reports');
    } else if (props.report?.slug) {
      // If on other pages (like ReportGrade or ReportGateway), go to report overview
      router.push({
        name: 'report-overview',
        params: { id: props.report.slug },
      });
    }
  }

  const navigationText = computed(() => {
    if (route.name === 'report-overview') {
      return 'Back to All Reports';
    }
    return 'Report Overview';
  });
</script>

<style lang="scss" scoped>
  .header-navigation {
    left: 0;
    width: 100%;
    background-color: rgb(var(--v-theme-primary));
    padding-bottom: 38px;

    .header-navigation-btn-container {
      margin-right: -20px;
    }

    .go-back {
      cursor: pointer;
    }

    .v-container {
      position: relative;
      z-index: 10;
    }

    &.mobile-header {
      top: 0;
      z-index: 10;
      padding-bottom: 10px;

      .v-container {
        position: relative;
        padding-top: 10px;
      }
    }

    .mobile-top-row {
      margin-bottom: 10px;
    }

    .mobile-grades-row {
      .v-col {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }

  .custom-panels.mobile-grade-panels {
    background: transparent !important;
    box-shadow: none !important;
    border: 1px solid rgba(var(--v-theme-white), 0.5);
    border-radius: 4px;

    :deep(.v-expansion-panel) {
      background: transparent !important;
      border: none !important;

      .v-expansion-panel-title__icon {
        color: white !important;
      }
    }
    :deep(.v-expansion-panel-title) {
      min-height: unset !important;
      padding: 8px 12px !important;
      border: none !important;
    }
    :deep(.v-expansion-panel-text__wrapper) {
      padding: 8px;
      background-color: transparent !important;
    }
    :deep(.v-expansion-panel::after) {
      border: none !important;
    }
  }

  .grades-navigation.mobile-grades-inside {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;

    .grades-navigation-button {
      margin: 4px 0;
      justify-content: flex-start;
      background-color: rgba(var(--v-theme-white), 0.1);

      &.v-btn--active {
        background-color: rgba(var(--v-theme-white), 0.3);
      }
    }
  }

  .grades-navigation-title {
    color: rgb(var(--v-theme-white));
    margin-right: 12px;
  }

  .grades-navigation:not(.mobile-grades-inside) {
    display: flex;
    align-items: center;
    justify-content: center;

    .grades-navigation-button {
      margin: 0 4px;

      &.v-btn--active {
        background-color: rgb(var(--v-theme-white));
        color: rgb(var(--v-theme-primary)) !important;
      }
    }
  }

  /* Print-specific styles to ensure all content is visible */
  @media print {
    /* Hide the entire header navigation in print view */
    .header-navigation {
      display: none !important;
    }

    /* Force color printing for charts, indicators, and backgrounds */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* Remove v-card white backgrounds that create overlays */
    :deep(.v-card) {
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    /* Specifically target SeriesPerformanceSnapshot card */
    :deep(.series-performance-snapshot.v-card),
    :deep(.v-card:has(.performance-snapshot)) {
      background: transparent !important;
      background-color: transparent !important;
    }

    /* Ensure the table remains visible with proper background */
    :deep(.performance-table),
    :deep(.v-table) {
      background: white !important;
      border: 1px solid #ccc !important;
    }

    :deep(.performance-table th),
    :deep(.performance-table td),
    :deep(.v-table th),
    :deep(.v-table td) {
      background: white !important;
    }

    /* Ensure chart elements, dots, and grade numbers are visible */
    :deep(.chart-dot),
    :deep(.grade-number),
    :deep(.status-dot),
    :deep(.score-value),
    :deep(.indicator-dot),
    :deep(svg),
    :deep(circle),
    :deep(rect) {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    /* Hide interactive elements that don't make sense in print */
    .header-navigation-btn-container {
      display: none !important;
    }

    /* Fix container widths for print */
    :deep(.v-container),
    :deep(.v-row),
    :deep(.v-col) {
      max-width: 100% !important;
      overflow: visible !important;
    }

    /* ========== READMORE PRINT FIXES ========== */
    /* Hide ReadMore buttons in print since they're not needed */
    :deep(.read-more-wrapper .read-more-btn),
    :deep(.read-more-btn) {
      display: none !important;
    }
  }
</style>
